import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  type BlockGuide,
  blockGuideBySlug,
  blockMinutes,
  blockOutline,
} from "../lib/blocking";
import {
  ANDROID_CAVEAT,
  ANDROID_TIMER,
  IOS_APP_LIMIT,
  IOS_DOWNTIME,
} from "../lib/blocking/steps";
import { articleBySlug } from "../lib/content";
import { prayerBySlug } from "../lib/prayers";
import { formatDate } from "./ArticleBody";
import { Contents, FaqList, renderBlock, ShortAnswer } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * One app, and how to stop opening it.
 *
 * ── Why the app's own design comes before the instructions ───────────────────
 *
 * Because the instructions are the same on every one of these pages and the
 * design is not. A reader who has already tried Screen Time twice does not need
 * a third recital of the menu path; they need to know why it did not hold, and
 * that answer is specific to the app they are asking about.
 *
 * The steps are still here, in full, near the top — somebody arriving from
 * "how to block X on iPhone" is owed the actual steps without a scroll. They
 * are just not the argument.
 */

function Numbered({
  id,
  heading,
  steps,
}: {
  id: string;
  heading: string;
  steps: string[];
}) {
  return (
    <section className="mt-10">
      <h2
        id={id}
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        {heading}
      </h2>
      <ol className="mt-5 flex flex-col gap-4">
        {steps.map((step, i) => (
          <li key={step.slice(0, 40)} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-pill border-[2.5px] border-ink bg-surface font-display text-sm font-bold text-ink"
            >
              {i + 1}
            </span>
            <span className="leading-[1.75] text-body">{step}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function CardShell({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-bold text-ink">{heading}</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">{children}</ul>
    </section>
  );
}

const CARD =
  "brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5";
const CARD_TITLE = "font-display text-lg leading-snug font-bold text-ink";
const CARD_TEXT = "text-sm leading-relaxed text-ink2";

function OtherApps({ slugs }: { slugs: string[] }) {
  const items = slugs.map(blockGuideBySlug).filter((g) => g !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="Other apps">
      {items.map((item) => (
        <li key={item.slug}>
          <Link to="/block/$slug" params={{ slug: item.slug }} className={CARD}>
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

function Prayers({ slugs }: { slugs: string[] }) {
  const items = slugs.map(prayerBySlug).filter((p) => p !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="Prayers">
      {items.map((item) => (
        <li key={item.slug}>
          <Link
            to="/prayers/$slug"
            params={{ slug: item.slug }}
            className={CARD}
          >
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

function ReadNext({ slugs }: { slugs: string[] }) {
  const items = slugs.map(articleBySlug).filter((a) => a !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="Read next">
      {items.map((item) => (
        <li key={item.slug}>
          <Link to="/blog/$slug" params={{ slug: item.slug }} className={CARD}>
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

export function BlockGuidePage({ guide }: { guide: BlockGuide }) {
  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {formatDate(guide.published)}
        {guide.updated ? ` · updated ${formatDate(guide.updated)}` : ""} ·{" "}
        {blockMinutes(guide)} min read
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        {guide.h1}
      </h1>
      <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {guide.standfirst}
      </p>

      <ShortAnswer takeaways={guide.takeaways} />
      <Contents
        headings={[
          { id: "on-iphone", text: "On iPhone" },
          {
            id: "why-the-limit-fails",
            text: `Why the limit fails on ${guide.app}`,
          },
          { id: "on-android", text: "On Android" },
          ...blockOutline(guide),
        ]}
      />

      <Numbered
        id="on-iphone"
        heading="On iPhone, step by step"
        steps={IOS_APP_LIMIT}
      />
      <Numbered
        id="downtime"
        heading="And the evening window"
        steps={IOS_DOWNTIME}
      />

      <section className="mt-12">
        <h2
          id="why-the-limit-fails"
          className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
        >
          What makes {guide.app} different
        </h2>
        {guide.mechanic.map((line) => (
          <p key={line.slice(0, 48)} className="mt-4 leading-[1.75] text-body">
            {line}
          </p>
        ))}
        <div className="brutal mt-6 p-5 sm:p-6">
          <p className="font-display text-sm font-bold tracking-wide text-scripture uppercase">
            Why the limit fails here
          </p>
          <p className="mt-3 leading-[1.75] text-body">{guide.limitFails}</p>
        </div>
        <h3 className="mt-8 font-display text-xl font-bold text-ink">
          What to do instead
        </h3>
        <ul className="mt-4 flex flex-col gap-3">
          {guide.instead.map((line) => (
            <li
              key={line.slice(0, 40)}
              className="border-l-[3px] border-brand pl-4 leading-[1.75] text-body"
            >
              {line}
            </li>
          ))}
        </ul>
      </section>

      <Numbered
        id="on-android"
        heading="On Android"
        steps={[...ANDROID_TIMER, ANDROID_CAVEAT]}
      />

      {guide.body.map(renderBlock)}

      <section className="mt-12">
        <h2 className="font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink">
          What Scripture has to say about it
        </h2>
        <ol className="mt-6 flex flex-col gap-5">
          {guide.verses.map((pick) => (
            <li key={pick.verse.ref} className="brutal p-5 sm:p-6">
              <p className="font-display text-sm font-bold tracking-wide text-scripture uppercase">
                {pick.verse.ref}
              </p>
              <figure className="mt-4 border-l-[3px] border-scripture bg-scripture-wash/60 py-4 pr-4 pl-5">
                <blockquote className="font-display text-[clamp(1.05rem,2.5vw,1.25rem)] leading-snug text-scripture">
                  “{pick.verse.text}”
                </blockquote>
              </figure>
              <p className="mt-4 leading-[1.75] text-body">{pick.note}</p>
            </li>
          ))}
        </ol>
      </section>

      {guide.faq ? <FaqList items={guide.faq} /> : null}

      <StoreCta from={`block:${guide.slug}`} />

      <OtherApps slugs={guide.relatedApps ?? []} />
      <Prayers slugs={guide.relatedPrayers ?? []} />
      <ReadNext slugs={guide.related} />
    </article>
  );
}
